/**
 * Route metadata for automatic journey tracking enrichment.
 * Maps URL path patterns to featureArea and entityType.
 * JourneyTracker uses this to enrich page_view events automatically.
 */

const ROUTE_METADATA = {
  // Dashboard
  "/": { featureArea: "dashboard", entityType: "dashboard" },
  "/dashboard": { featureArea: "dashboard", entityType: "dashboard" },

  // Participants
  "/participant-list": { featureArea: "participants", entityType: "participant" },
  "/sp-profile": { featureArea: "participants", entityType: "participant" },
  "/participants": { featureArea: "participants", entityType: "participant" },
  "/participant-profile": { featureArea: "participants", entityType: "participant" },

  // HRM
  "/hrm": { featureArea: "hrm", entityType: "dashboard" },
  "/hrm/team-management": { featureArea: "hrm", entityType: "worker" },
  "/hrm/all-applicants": { featureArea: "hrm", entityType: "applicant" },
  "/hrm/applicant-profile": { featureArea: "hrm", entityType: "applicant" },
  "/hrm/applicant-assessment": { featureArea: "hrm", entityType: "applicant" },
  "/hrm/all-managers": { featureArea: "hrm", entityType: "manager" },
  "/worker-profile": { featureArea: "hrm", entityType: "worker" },

  // Rostering
  "/rostering/dashboard": { featureArea: "rostering", entityType: "dashboard" },
  "/rostering/schedule": { featureArea: "rostering", entityType: "schedule" },
  "/rostering/timesheet": { featureArea: "rostering", entityType: "timesheet" },
  "/rostering/timesheetExport": { featureArea: "rostering", entityType: "timesheet" },
  "/rostering/leaverequests": { featureArea: "rostering", entityType: "leave_request" },
  "/rostering/invoicing": { featureArea: "rostering", entityType: "invoice" },
  "/rostering/create-roster-invoice": { featureArea: "rostering", entityType: "invoice" },
  "/rostering/roster-invoice-preview": { featureArea: "rostering", entityType: "invoice" },
  "/rostering/invoicing/dashboard": { featureArea: "rostering", entityType: "invoice" },
  "/rostering/invoicing/allocation": { featureArea: "rostering", entityType: "timesheet" },

  // COS
  "/cos/dashboard": { featureArea: "cos", entityType: "dashboard" },
  "/cos/participant-list": { featureArea: "cos", entityType: "participant" },
  "/cos/participant-profile": { featureArea: "cos", entityType: "participant" },
  "/cos/ndis-claims": { featureArea: "cos", entityType: "ndis_claim" },
  "/cos/invoice": { featureArea: "cos", entityType: "invoice" },
  "/cos/settings": { featureArea: "cos", entityType: "settings" },
  "/cos/catalog-services": { featureArea: "cos", entityType: "catalog_service" },
  "/cos/external-service-provider": { featureArea: "cos", entityType: "external_provider" },
  "/cos/casenotes-history": { featureArea: "cos", entityType: "case_note" },
  "/cos/reporting": { featureArea: "cos", entityType: "report" },

  // Allied Health
  "/ah/dashboard": { featureArea: "allied_health", entityType: "dashboard" },
  "/ah/participant-list": { featureArea: "allied_health", entityType: "participant" },
  "/ah/participant-profile": { featureArea: "allied_health", entityType: "participant" },
  "/ah/ndis-claims": { featureArea: "allied_health", entityType: "ndis_claim" },
  "/ah/invoice": { featureArea: "allied_health", entityType: "invoice" },
  "/ah/settings": { featureArea: "allied_health", entityType: "settings" },
  "/ah/catalog-services": { featureArea: "allied_health", entityType: "catalog_service" },
  "/ah/casenotes-history": { featureArea: "allied_health", entityType: "case_note" },
  "/ah/reporting": { featureArea: "allied_health", entityType: "report" },

  // SIL
  "/sil-accommodation": { featureArea: "sil", entityType: "accommodation" },
  "/forms-accommodation": { featureArea: "sil", entityType: "form_template" },

  // Incident Management
  "/incident-management": { featureArea: "incident_management", entityType: "incident" },

  // Settings
  "/settings": { featureArea: "settings", entityType: "personal_setting" },
  "/settings/sp-setting": { featureArea: "settings", entityType: "sp_setting" },
  "/settings/worker-compliance-setting": { featureArea: "settings", entityType: "compliance_setting" },
  "/settings/rostering-setting": { featureArea: "settings", entityType: "rostering_setting" },
  "/settings/region-setting": { featureArea: "settings", entityType: "region_setting" },
  "/settings/incident-reporting-setting": { featureArea: "settings", entityType: "incident_setting" },
  "/settings/branding": { featureArea: "settings", entityType: "branding" },
  "/settings/funding-setting": { featureArea: "settings", entityType: "funding_setting" },
  "/settings/ai-settings": { featureArea: "settings", entityType: "ai_settings" },
  "/settings/ai-usage": { featureArea: "settings", entityType: "ai_credits_usage" },
  "/settings/ai": { featureArea: "settings", entityType: "ai_credits_usage" },
  "/notification-setting": { featureArea: "settings", entityType: "notification_setting" },
  "/roles-permissions-setting": { featureArea: "settings", entityType: "role_permission" },
  "/payroll-setting": { featureArea: "settings", entityType: "payroll_setting" },

  // Subscription
  "/subscription": { featureArea: "subscription", entityType: "subscription" },

  // Forms
  "/forms-list": { featureArea: "forms", entityType: "form_template" },
  "/forms-list-hrm": { featureArea: "forms", entityType: "form_template" },

  // Reporting
  "/reporting": { featureArea: "reporting", entityType: "dashboard" },

  // Complaints, Feedback, Policies
  "/complaints": { featureArea: "complaints", entityType: "complaint" },
  "/feedbacks": { featureArea: "feedback", entityType: "feedback" },
  "/policies": { featureArea: "policies", entityType: "policy" },
  "/ask-a-question": { featureArea: "support", entityType: "question" },

  // Documents
  "/documents": { featureArea: "documents", entityType: "document" },

  // Audit & Sessions
  "/audit-log": { featureArea: "audit", entityType: "audit_log" },
  "/user-sessions": { featureArea: "user_sessions", entityType: "session" },

  // Auth
  "/signup": { featureArea: "auth", entityType: "sign_up" },
  "/forget-password": { featureArea: "auth", entityType: "password_reset" },
  "/reset-password": { featureArea: "auth", entityType: "password_reset" },
  "/invite-member": { featureArea: "hrm", entityType: "team_member" },
};

/**
 * Get metadata for a given pathname. Matches exact paths first,
 * then tries prefix matching, then infers from URL segments.
 */
export function getRouteMetadata(pathname) {
  if (!pathname) return { featureArea: "general", entityType: null };

  const cleanPath = pathname.replace(/\/+$/, "") || "/";

  if (ROUTE_METADATA[cleanPath]) {
    return ROUTE_METADATA[cleanPath];
  }

  for (const [pattern, meta] of Object.entries(ROUTE_METADATA)) {
    if (cleanPath.startsWith(pattern + "/") || cleanPath.startsWith(pattern + "?")) {
      return meta;
    }
  }

  const segments = cleanPath.split("/").filter(Boolean);
  if (segments.length > 0) {
    const firstSegment = segments[0].replace(/-/g, "_");
    return { featureArea: firstSegment, entityType: segments[1]?.replace(/-/g, "_") || null };
  }

  return { featureArea: "general", entityType: null };
}

export default ROUTE_METADATA;
